import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  IWizardContext,
  persistWizardStepData,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnMultiSelect,
  ZnSelectQueryParams,
} from '@zendra/shared';
import {
  SelectValue,
  STAND_CYCLE_ROTATION_COLUMN_HEADER_KEYS,
  toMultiSelectValues,
} from '../contract-assignment-by-stand.shared';
import { ContractAssociation } from '../services/contract-association';
import { tap } from 'rxjs';
import { IAssociatePropertyStepData } from '../interfaces/IAssociatePropertyStepData';
import { IAssociateStandCycleRotationStepData } from '../interfaces/IAssociateStandCycleRotation';
import { formatDateOnlyFieldValue } from 'projects/shared/src/lib/utils/date-only-value';
import { IStandCycleRotationRow } from '../interfaces/IStandCycleRotationRow';
import { StandCycleRotationAssociationDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/contract-association/models';

@Component({
  selector: 'lib-step-associate-stand-cycle-rotation',
  standalone: true,
  imports: [LocalizationPipe, ZnMultiSelect],
  templateUrl: './step-associate-stand-cycle-rotation.html',
})
export class StepAssociateStandCycleRotation
  implements WizardStepComponent<IAssociateStandCycleRotationStepData>
{
  private readonly toaster = inject(ToasterService);
  private readonly contractAssociationService = inject(ContractAssociation);

  readonly standCycleRotationSelect = viewChild('standCycleRotationSelect', {
    read: ZnMultiSelect,
  });

  readonly localizationKeyPrefix =
    'Geographic::ContractAssignmentByStand.StepAssociateStandCycleRotation';

  // readonly standCycleRotationOptions = signal(STAND_CYCLE_ROTATION_OPTIONS);
  readonly standCycleRotationOptions = signal<SelectOption<string>[]>([]);
  readonly columnHeaderKeys = STAND_CYCLE_ROTATION_COLUMN_HEADER_KEYS;

  readonly selectedStandCycleRotations = signal<string[]>([]);
  readonly rows = signal<IStandCycleRotationRow[]>([]);

  readonly selectionSectionClass = computed(() =>
    this.rows().length > 0
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );

  readonly tableSectionClass = computed(() =>
    this.rows().length > 0
      ? 'zn-full-height zn-step-content-section zn-step-content-section--selected'
      : 'zn-full-height zn-step-content-section zn-step-content-section--disabled',
  );

  private propertyId: number = 0;
  private readonly standCycleRotations = new Map<string, StandCycleRotationAssociationDto>();
  private readonly standCycleRotationRowsById = new Map<string, IStandCycleRotationRow>();

  loadStandCycleRotations = (params?: ZnSelectQueryParams) => this.contractAssociationService.loadStandCycleRotations({
    filter: params?.search ?? null,
    maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
    propertyId: this.propertyId,
    skipCount: params?.skipCount,
  }).pipe(
      tap(response => {
        response.items.forEach(item => {
          this.standCycleRotations.set(item.key, item);
          this.cacheStandCycleRotationRow(this.mapStandCycleRotationToRow(item.key, item));
        });
        this.mergeStandCycleRotationOptions(
          response.items.map(item => this.toStandCycleRotationOption(item.key, item)),
        );
      }),
    );

  onStandCycleRotationChange(value: SelectValue): void {
    const selectedIds = this.resolveSelectedIds(value);
    const selectedRows = this.buildStandCycleRotationRows(selectedIds);
    this.selectedStandCycleRotations.set(selectedIds);
    this.rows.set(selectedRows);    
    this.mergeStandCycleRotationOptions(
      selectedRows.map(row => this.toStandCycleRotationOptionFromRow(row)),
    );
  }

  removeRow(key: string): void {
    const selectedIds = this.selectedStandCycleRotations().filter(selectedId => selectedId !== key);
    this.selectedStandCycleRotations.set(selectedIds);
    this.rows.set(this.buildStandCycleRotationRows(selectedIds));
    this.standCycleRotationOptions.set(
      this.rows().map(row => this.toStandCycleRotationOptionFromRow(row)),
    );

    this.toaster.success(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepAssociateStandCycleRotation.removeSuccess`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );

    this.writeDropdown(selectedIds);
  }

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });
    const associateProperty: IAssociatePropertyStepData | undefined = getWizardStepData(context, context.activeStepId() -1);
    this.propertyId = Number(associateProperty?.propertyId)

    const previousData: IAssociateStandCycleRotationStepData | undefined = getWizardStepData(context, context.activeStepId());
    if(previousData) this.loadPreviousData(previousData, context);
  }

  canLeaveSync(): boolean {
    return true;
  }

  canLeave(): boolean {
    if (this.rows().length > 0) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepAssociateStandCycleRotation.cantContinue`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  collect(context: IWizardContext): IAssociateStandCycleRotationStepData {
    const data: IAssociateStandCycleRotationStepData = {
      standCycleRotationRows: this.rows(),
      rowsIds: this.selectedStandCycleRotations()
    };
    persistWizardStepData(context, {
      valid: true,
      visited: this.rows().length > 0,
      completed: true,
      data: data,
      dirty: true,
      meta: buildWizardStepDependencyMeta(data),
      invalidateStepIds: [4],
    });
    return data;
  }

  private loadPreviousData(data: IAssociateStandCycleRotationStepData, context: IWizardContext): void {
    this.selectedStandCycleRotations.set(data.rowsIds);
    this.rows.set(data.standCycleRotationRows);
    data.standCycleRotationRows.forEach(row => {
      this.cacheStandCycleRotationRow(row);
    });
    this.standCycleRotationOptions.set(
      data.standCycleRotationRows.map(row => this.toStandCycleRotationOptionFromRow(row)),
    );

    this.writeDropdown(data.rowsIds);

    context.patchStepState(context.activeStepId(), {
      visited: true,
      dirty: true,
      completed: true,
      data: data
    });
  }

  private writeDropdown(selectedIds: string[]) {
    queueMicrotask(() => {
      this.standCycleRotationSelect()?.writeValue(selectedIds);
    });
  }

  private buildStandCycleRotationRows(selectedIds: string[]): IStandCycleRotationRow[] {
    return selectedIds
      .map(id => this.resolveStandCycleRotationRow(id))
      .filter((row): row is IStandCycleRotationRow => !!row);
  }

  private resolveSelectedIds(value: SelectValue): string[] {
    const selectedIds = toMultiSelectValues(value);
    if (selectedIds.length > 0) {
      return selectedIds;
    }

    if (!this.standCycleRotationSelect()?.allSelected()) {
      return selectedIds;
    }

    return this.standCycleRotationOptions().map(option => option.key);
  }

  private resolveStandCycleRotationRow(id: string): IStandCycleRotationRow | undefined {
    const standCycleRotation = this.standCycleRotations.get(id);
    if (standCycleRotation) {
      const row = this.mapStandCycleRotationToRow(id, standCycleRotation);
      this.cacheStandCycleRotationRow(row);
      return row;
    }

    return this.standCycleRotationRowsById.get(id);
  }

  private mapStandCycleRotationToRow(
    key: string,
    standCycleRotation: StandCycleRotationAssociationDto,
  ): IStandCycleRotationRow {
    return {
      adjustmentType: standCycleRotation.adjustmentTypeName ?? '',
      area: standCycleRotation.standArea ?? 0,
      cycle: standCycleRotation.cycle ?? 0,
      geneticMaterial: standCycleRotation.geneticMaterialName ?? '',
      key,
      maintenancePeriod: standCycleRotation.maintenancePeriodName ?? '',
      maintenanceReason: standCycleRotation.maintenanceReasonName ?? '',
      nearbyPopulation: standCycleRotation.hasNearbyPopulation ?? false,
      ownPlantation: standCycleRotation.isOwnPlantation ?? false,
      plantationDate: formatDateOnlyFieldValue(standCycleRotation.plantationDate ?? '') ?? '',
      rotation: standCycleRotation.rotation ?? 0,
      spacing: standCycleRotation.spacingName ?? '',
      plantationType: standCycleRotation.plantationTypeName ?? '',
      species: standCycleRotation.speciesName ?? '',
      stand: standCycleRotation.standName ?? '',
      standType: standCycleRotation.standTypeName ?? '',
    };
  }

  private toStandCycleRotationOption(
    key: string,
    standCycleRotation: StandCycleRotationAssociationDto,
  ): SelectOption<string> {
    return {
      key,
      value: this.buildStandCycleRotationOptionValue(
        standCycleRotation.standName ?? '',
        standCycleRotation.cycle,
        standCycleRotation.rotation,
      ),
    };
  }

  private toStandCycleRotationOptionFromRow(row: IStandCycleRotationRow): SelectOption<string> {
    return {
      key: row.key,
      value: this.buildStandCycleRotationOptionValue(row.stand, row.cycle, row.rotation),
    };
  }

  private buildStandCycleRotationOptionValue(
    stand: string,
    cycle?: number,
    rotation?: number,
  ): string {
    return `${stand} - ${cycle ?? ''} - ${rotation ?? ''}`;
  }

  private cacheStandCycleRotationRow(row: IStandCycleRotationRow): void {
    this.standCycleRotationRowsById.set(row.key, row);
  }

  private mergeStandCycleRotationOptions(options: SelectOption<string>[]): void {
    const optionsByKey = new Map(
      this.standCycleRotationOptions().map(option => [option.key, option] as const),
    );

    options.forEach(option => {
      optionsByKey.set(option.key, option);
    });

    this.standCycleRotationOptions.set(Array.from(optionsByKey.values()));
  }
}
